import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { UboOwnershipLevel } from '../../interfaces/ubo/ownership-level';
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { Order } from '../../interfaces/orders/order';
import { Document } from '../../interfaces/documents/document';
import { eDocumentType, eOrderType, ePartyType } from '../../enums';
import { UboTreeNode, UboTreeNodeForCreation } from '../../interfaces/hub-reports/ubo-tree-node';
import { PatchDocument } from '../../interfaces/patch-document';
import { BapiResponse, RegHubApiResult } from '../../interfaces/reghubapi-result';
import { ValidationProblem } from '../../interfaces/validation/validation-problem';

@Injectable({
  providedIn: 'root',
})
export class UboTreeNodesService {
  private base: string = 'api/v1/UboTrees';

  public currentNodeValidationErrors$ = new BehaviorSubject<ValidationProblem[]>([]);
  public linkToHubReportNodes = new BehaviorSubject<UboTreeNode[]>([]);
  public linkToHubReportNodesDistinctByHubReport = new BehaviorSubject<UboTreeNode[]>([]);

  constructor(
    private client: HttpClient,
    @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) {
      this.base = `${this.config.rootUrl}/UboTrees`;
    }

  public getUboTree(rootNodeID: string): Observable<UboTreeNode> {
    return this.client.get<UboTreeNode>(`${this.base}/${rootNodeID}`);
  }

  public getUboTreeWithLists(rootNodeID: string): Observable<UboTreeNode> {
    let headers = new HttpHeaders().append('accept', 'application/vnd.reghub.ubo-tree-with-lists+json');
    return this.client.get<UboTreeNode>(`${this.base}/${rootNodeID}`, { headers: headers});
  }

  public validateUboTreeNode(rootNodeID: string): Observable<BapiResponse<UboTreeNode>> {
    let headers = new HttpHeaders().append('accept', 'application/vnd.reghub.ubo-tree-with-validation+json');
    return this.client.get<BapiResponse<UboTreeNode>>(`${this.base}/${rootNodeID}`, { headers: headers})
      .pipe(tap(response => this.currentNodeValidationErrors$.next(response.validation?.errors ?? [])));
  }

  public getUboTreeWithListsAndValidation(rootNodeID: string): Observable<BapiResponse<UboTreeNode>> {
    let headers = new HttpHeaders().append('accept', 'application/vnd.reghub.ubo-tree-with-lists-and-validation+json');
    return this.client.get<BapiResponse<UboTreeNode>>(`${this.base}/${rootNodeID}`, { headers: headers})
      .pipe(tap(response => this.currentNodeValidationErrors$.next(response.validation?.errors ?? [])));
  }

  public getUboTrees(params?: { parentID?: string, orderID?: string, businessNumber?: string, hasChildren?: boolean, businessCountry?: string, businessJurisdiction?: string, partyTypes?: ePartyType[], includesNoOrderOfTypes?: eOrderType[], createdForOrderID?: string }, emitEvent: boolean = false, withLists: boolean = false) {
    let headers = new HttpHeaders();

    if (withLists) {
      headers = headers.set('accept', 'application/vnd.reghub.ubo-tree-with-lists+json');
    }

    let parameters = new HttpParams();

    if (params) {
      if (params.parentID) {
        parameters = parameters.set('parentID', params.parentID);
      }
      
      if (params.orderID) {
        parameters = parameters.set('orderID', params.orderID);
      }
      
      if (params.businessNumber) {
        parameters = parameters.set('businessNumber', params.businessNumber);
      }

      if (params.hasChildren !== undefined) {
        parameters = parameters.set('hasChildren', params.hasChildren);
      }

      if (params.businessCountry) {
        parameters = parameters.set('businessCountry', params.businessCountry);
      }

      if (params.businessJurisdiction) {
        parameters = parameters.set('businessJurisdiction', params.businessJurisdiction);
      }

      if (params.partyTypes) {
        for(let i = 0; i < params.partyTypes.length; i++) {
          parameters = parameters.append('partyType', params.partyTypes[i]);
        }
      }

      if (params.includesNoOrderOfTypes) {
        for(let i = 0; i < params.includesNoOrderOfTypes.length; i++) {
          parameters = parameters.append('includesNoOrderTypeOf', params.includesNoOrderOfTypes[i]);
        }
      }

      if (params.createdForOrderID) {
        parameters = parameters.set('createdForOrderID', params.createdForOrderID);
      }
    }

    return this.client.get<UboTreeNode[]>(this.base, { params: parameters, headers: headers })
      .pipe(tap(nodes => {
        if (emitEvent) {
          this.linkToHubReportNodes.next(nodes);
          this.linkToHubReportNodesDistinctByHubReport.next([...new Map(nodes.map(n => [n['hubReportID'], n])).values()]);
        }
      }));
  }

  public getOwnershipLevelsForNode(nodeID: string): Observable<UboOwnershipLevel[]> {
    return this.client.get<UboOwnershipLevel[]>(`${this.base}/${nodeID}/OwnershipLevels`);
  }

  public getOrdersForNode(nodeID: string): Observable<Order[]> {
    return this.client.get<Order[]>(`${this.base}/${nodeID}/Orders`);
  }

  public getNodeDocument(nodeID: string, document: Document){
    let headers = new HttpHeaders();
    
    if(document.documentTypeID === eDocumentType.pdf) {
      headers = headers.append('accept', 'application/pdf');
    }

    return this.client.get(`${this.base}/${nodeID}/Document`, { headers: headers, responseType: 'blob' });
  }

  public addOrderToNode(nodeID: string, orderID: string) {
    return this.client.post(`${this.base}/${nodeID}/Orders`, { orderID: orderID });
  }

  public addUboTreeNode(parentNodeID: string, treeNode: UboTreeNodeForCreation): Observable<BapiResponse<UboTreeNode>> {
    let headers = new HttpHeaders().append('accept', 'application/vnd.reghub.ubo-tree-with-lists+json');
    return this.client.post<BapiResponse<UboTreeNode>>(`${this.base}/${parentNodeID}/Children`, treeNode, { headers: headers})
      .pipe(tap(response => this.currentNodeValidationErrors$.next(this.currentNodeValidationErrors$.value.concat(response.validation?.errors ?? []))))
  }

  public patchUboTreeNode(node: UboTreeNode, patchDocument: PatchDocument[]): Observable<BapiResponse<UboTreeNode>> {
    return this.client.patch<BapiResponse<UboTreeNode>>(`${this.base}/${node.id}`, patchDocument)
      .pipe(tap(response => this.currentNodeValidationErrors$.next(this.filterErrors(node).concat(response.validation?.errors ?? []))));
  }

  public deleteUboTreeNode(node: UboTreeNode) {
    return this.client.delete(`${this.base}/${node.id}`)
      .pipe(tap(() => this.currentNodeValidationErrors$.next(this.filterErrors(node))));
  }

  private filterErrors(node: UboTreeNode) {
    const errors = this.currentNodeValidationErrors$.value;
    return errors.filter(e => e.entityID !== node.id);
  }

  public clearSubjects() {
    this.linkToHubReportNodes.next([]);
    this.linkToHubReportNodesDistinctByHubReport.next([]);
  }
}
